export default function initDropdownGrid() {
    const $dropdownContainers = document.querySelectorAll<HTMLElement>('[data-dropdown-grid]');
    $dropdownContainers.forEach($container => {
        new DropDownGrid($container);
    });
}

/**
 * Rend un élement accordéon
 * mettre data-dropdown-grid="mobile" ou data-dropdown-grid="desktop" pour une activation mobile ou desktop only
 * 
 * Utilisation :
 * <div data-dropdown-grid>
 *   <div data-dropdown-grid-button>Toggle menu</div>
 *   <div data-dropdown-grid-content>
 *   </div>
 * </div>
 */
export class DropDownGrid {

    $container: HTMLElement | null;
    $button: HTMLElement | null;
    $content: HTMLElement | null;
    mdBreakpoint: number;
    device: string;
    deviceParam: string;
    callEvent: () => void;
    callEventOutside: (event: Event) => void;
    callEventEscape: (event: KeyboardEvent) => void;

    constructor($container: HTMLElement | null) {
        this.$container = $container;
        this.$button = $container?.querySelector('[data-dropdown-grid-button]') ?? null;
        this.$content = $container?.querySelector('[data-dropdown-grid-content]') ?? null;
        this.mdBreakpoint = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--breakpoint-md'));
        this.device = '';
        this.deviceParam = this.$container?.dataset.dropdownGrid ?? '';
        this.callEvent = () => this.dropdownClickHandler();
        this.callEventOutside = (event: Event) => this.dropdownClickOutsideHandler(event as MouseEvent);
        this.callEventEscape = (event: KeyboardEvent) => this.dropdownEscapeHandler(event);

        this.init(this.deviceParam);
    }

    init(deviceParam: string): void {
        if (deviceParam === 'mobile') {
            window.addEventListener('resize', this.initMobileOnly.bind(this));
            this.initMobileOnly();
        } else if (deviceParam === 'desktop') {
            window.addEventListener('resize', this.initDesktopOnly.bind(this));
            this.initDesktopOnly();
        } else {
            this.initDropDown();
        }
    }

    initMobileOnly(): void {
        if (this.device !== 'desktop' && window.innerWidth > this.mdBreakpoint) {
            this.device = 'desktop';
            this.destroyDropDown();
        } else if (this.device !== 'mobile' && window.innerWidth <= this.mdBreakpoint) {
            this.device = 'mobile';
            this.initDropDown();
        }
    }

    initDesktopOnly(): void {
        if (this.device !== 'desktop' && window.innerWidth > this.mdBreakpoint) {
            this.device = 'desktop';
            this.initDropDown();
        } else if (this.device !== 'mobile' && window.innerWidth <= this.mdBreakpoint) {
            this.device = 'mobile';
            this.destroyDropDown();
        }
    }

    /**
     * Initialise les éléments du dropdown
     */
    initDropDown(): void {
        this.initAria();
        this.$button?.addEventListener('click', this.callEvent);
        document.addEventListener('click', this.callEventOutside);
        document.addEventListener('keydown', this.callEventEscape);
    }

    /**
     * Supprime les éléments du dropdown
     */
    destroyDropDown(): void {
        this.deleteAria();
        this.$button?.removeEventListener('click', this.callEvent);
        document.removeEventListener('click', this.callEventOutside);
        document.removeEventListener('keydown', this.callEventEscape);
    }

    /**
     * Initialise les aria-expanded et aria hidden
     */
    initAria(): void {
        this.$button?.setAttribute('tabindex', '0');
        this.$button?.setAttribute('role', 'button');
        this.$content?.setAttribute('aria-hidden', 'true');
    }

    /**
     * Supprime les aria-expanded et aria hidden
     */
    deleteAria(): void {
        this.$button?.removeAttribute('tabindex');
        this.$button?.removeAttribute('role');
        this.$button?.removeAttribute('aria-expanded');
        this.$content?.removeAttribute('aria-hidden');
    }

    /**
     * Fonction au click sur le bouton dropdown
     */
    dropdownClickHandler(): void {
        if (!this.$container?.classList.contains('open')) {
            this.openDropdown();
        } else {
            this.closeDropdown();
        }
    }

    /**
     * Fonction au click à l'exterieur du dropdown
     */
    dropdownClickOutsideHandler(event: MouseEvent): void {
        if (this.$container && !this.$container.contains(event.target as Node)) {
            this.closeDropdown();
        }
    }

     /**
     * Fonction à la pression de la touche Escape
     */
     dropdownEscapeHandler(event: KeyboardEvent): void {
        if (event.key === 'Escape' && this.$container?.classList.contains('open')) {
            this.closeDropdown();
            this.$button?.focus(); // Redonner le focus au bouton après fermeture
        }
    }

    /**
     * Ouvre le dropdown
     */
    openDropdown(): void {
        this.$container?.classList.add('open');
        this.$button?.setAttribute('aria-expanded', 'true');
        this.$content?.setAttribute('aria-hidden', 'false');
    }

    /**
     * Ferme le dropdown
     */
    closeDropdown(): void {
        this.$container?.classList.remove('open');
        this.$button?.removeAttribute('aria-expanded');
        this.$content?.setAttribute('aria-hidden', 'true');
    }
}
